<template>
  <div class="account-codes-page">
    <div class="account-codes-container">
      <div class="page-header">
        <div>
          <button @click="goBack" class="btn-back">
            <LineIcon name="arrow-left" :size="15" :stroke-width="1.8" />
            {{ $t('companies.backToCompanies') }}
          </button>
          <h1 class="page-title">{{ company?.name }} - {{ $t('accountCodes.title') }}</h1>
          <p class="page-subtitle">{{ $t('accountCodes.subtitle', { company: company?.name }) }}</p>
        </div>
        <div class="header-actions">
          <button @click="showBulkImportModal = true" class="btn-secondary">
            <LineIcon name="file-import" :size="15" :stroke-width="1.8" />
            {{ $t('accountCodes.bulkImport', 'Bulk Import') }}
          </button>
          <button @click="openCreateModal" class="btn-create">
            <LineIcon name="plus" :size="15" :stroke-width="1.8" />
            {{ $t('accountCodes.addCode') }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="accountCodes.length === 0" class="empty-state">
        <LineIcon name="hash" :size="15" :stroke-width="1.8" style="font-size: 80px; color: #cbd5e1;" />
        <h3>{{ $t('accountCodes.noCodes') }}</h3>
        <p>{{ $t('accountCodes.noCodesDesc') }}</p>
        <button @click="openCreateModal" class="btn-primary">
          <LineIcon name="plus" :size="15" :stroke-width="1.8" />
          {{ $t('accountCodes.addCode') }}
        </button>
      </div>

      <div v-else class="account-codes-table">
        <table>
          <thead>
            <tr>
              <th>{{ $t('accountCodes.code') }}</th>
              <th>{{ $t('accountCodes.name') }}</th>
              <th>{{ $t('accountCodes.type') }}</th>
              <th>{{ $t('accountCodes.category') }}</th>
              <th>{{ $t('common.status') }}</th>
              <th>{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="code in accountCodes" :key="code.id">
              <td class="code-cell">{{ code.account_code }}</td>
              <td>{{ code.account_name }}</td>
              <td>
                <span class="type-badge" :class="`type-${code.account_type}`">
                  {{ code.account_type }}
                </span>
              </td>
              <td>{{ code.category || '—' }}</td>
              <td>
                <span class="status-badge" :class="code.is_active ? 'active' : 'inactive'">
                  {{ code.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td>
                <button @click="editAccountCode(code)" class="btn-icon" title="Edit">
                  <LineIcon name="pencil" :size="15" :stroke-width="1.8" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create/Edit Account Code Modal -->
    <AccountCodeModal
      v-if="showCreateModal || editingAccountCode"
      :accountCode="editingAccountCode"
      :companyId="companyId"
      :canGoPrevious="canGoPrevious"
      :canGoNext="canGoNext"
      @close="closeModal"
      @saved="handleSaved"
      @navigate="navigateAccountCode"
    />

    <!-- Bulk Import Modal -->
    <BulkImportAccountCodesModal
      v-if="showBulkImportModal"
      :companyId="companyId"
      @close="showBulkImportModal = false"
      @imported="handleBulkImported"
    />
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import api from '@/utils/api';
import AccountCodeModal from '@/components/companies/AccountCodeModal.vue';
import BulkImportAccountCodesModal from '@/components/companies/BulkImportAccountCodesModal.vue';

const router = useRouter();
const route = useRoute();

const companyId = computed(() => route.params.companyId);
const company = ref(null);
const accountCodes = ref([]);
const loading = ref(true);
const showCreateModal = ref(false);
const showBulkImportModal = ref(false);
const editingAccountCode = ref(null);

const loadCompany = async () => {
  try {
    const response = await api.get(`/api/companies/${companyId.value}`);
    company.value = response.data;
  } catch (error) {
    console.error('Failed to load company:', error);
  }
};

const loadAccountCodes = async () => {
  try {
    loading.value = true;
    const response = await api.get(`/api/companies/${companyId.value}/account-codes`);
    accountCodes.value = response.data.data || response.data;
  } catch (error) {
    console.error('Failed to load account codes:', error);
  } finally {
    loading.value = false;
  }
};

const editAccountCode = (code) => {
  editingAccountCode.value = code;
};

// Position of the open code within the list as it is shown in the table, so the
// arrows walk the same order the user is looking at.
const currentIndex = computed(() => {
  if (!editingAccountCode.value) return -1;
  return accountCodes.value.findIndex(code => code.id === editingAccountCode.value.id);
});

const canGoPrevious = computed(() => currentIndex.value > 0);
const canGoNext = computed(() =>
  currentIndex.value > -1 && currentIndex.value < accountCodes.value.length - 1
);

const navigateAccountCode = (step) => {
  const target = currentIndex.value + step;
  if (currentIndex.value === -1 || target < 0 || target >= accountCodes.value.length) {
    return;
  }
  editingAccountCode.value = accountCodes.value[target];
};

const openCreateModal = () => {
  editingAccountCode.value = null;
  showCreateModal.value = true;
};

const closeModal = () => {
  showCreateModal.value = false;
  editingAccountCode.value = null;
};

const handleSaved = () => {
  closeModal();
  loadAccountCodes();
};

const handleBulkImported = () => {
  showBulkImportModal.value = false;
  loadAccountCodes();
};

const goBack = () => {
  router.push('/companies');
};

onMounted(() => {
  loadCompany();
  loadAccountCodes();
});
</script>

<style scoped>
.account-codes-page {
  min-height: 100vh;
  background: var(--bg-gray);
  padding: 24px 20px;
}

.account-codes-container {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 20px;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;
}

.btn-back:hover {
  background: var(--bg-gray);
  color: var(--text-primary);
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  color: var(--text-primary);
  border: 2px solid var(--border);
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  border-color: var(--primary);
  color: var(--primary);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.2);
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-create:hover {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state svg {
  margin-bottom: 24px;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary:hover {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.account-codes-table {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-gray);
}

th {
  text-align: left;
  padding: 16px 20px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

tbody tr {
  border-bottom: 1px solid var(--border);
  transition: all 0.2s ease;
}

tbody tr:hover {
  background: var(--bg-gray);
}

tbody tr:last-child {
  border-bottom: none;
}

td {
  padding: 16px 20px;
  font-size: 14px;
  color: var(--text-primary);
}

.code-cell {
  font-weight: 600;
  font-family: monospace;
  color: var(--primary);
}

.type-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.type-expense {
  background: #fee2e2;
  color: #991b1b;
}

.type-revenue {
  background: #dcfce7;
  color: #166534;
}

.type-neutral {
  background: #f3f4f6;
  color: #374151;
}

.type-asset {
  background: #dbeafe;
  color: #1e40af;
}

.type-liability {
  background: #fef3c7;
  color: #92400e;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.active {
  background: var(--success-bg);
  color: var(--success-dark);
}

.status-badge.inactive {
  background: var(--gray-light);
  color: var(--text-secondary);
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  background: var(--primary-light);
  border-color: var(--primary);
  color: var(--primary);
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .account-codes-table {
    overflow-x: auto;
  }

  table {
    min-width: 600px;
  }
}

/* ── The rest of the buttons ───────────────────────────────────────────
 *
 * The chrome above settles the ones named primary, secondary, cancel and
 * save; these carry names of their own and so kept the old 15px-in-a-49px-box
 * shape. Same geometry as the others: a 32px box, a 13px label, 8px corner,
 * and a filled face only where something is being committed. */
.btn-create,
.btn-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  box-shadow: none;
  transition: all 0.15s ease;
  width: auto;
}

.btn-create {
  background: #0697e4;
  border: 1px solid #0697e4;
  color: #fff;
}

.btn-create:hover:not(:disabled) {
  background: #0b84c7;
  border-color: #0b84c7;
  box-shadow: none;
  transform: none;
}

.btn-back {
  background: #fff;
  border: 1px solid #dde3ea;
  color: #1e293b;
}

.btn-back:hover:not(:disabled) {
  background: #fff;
  border-color: #0697e4;
  color: #0697e4;
  box-shadow: none;
  transform: none;
}


/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}
</style>
